type ProgramLike = {
  isArchived?: boolean | null;
  state?: string | null;
  start?: unknown;
};

export function isProgramArchived(program: ProgramLike | null | undefined): boolean {
  if (!program) return false;
  return program.isArchived === true || program.state === 'ARCHIVED';
}

/**
 * Archived dossiers are admin-only: coaches lose them from their overview and
 * cannot open them by URL.
 */
export function canUserAccessProgram(
  userType: string | null | undefined,
  program: ProgramLike | null | undefined
): boolean {
  if (!program) return false;
  if (userType === 'ADMIN') return true;
  return !isProgramArchived(program);
}

/**
 * A participant may only log in while at least one of their dossiers is not
 * archived. Participants without any dossier keep access (they see the
 * "not assigned to a program" screen).
 */
export function isParticipantLoginBlocked(programs: ProgramLike[]): boolean {
  if (programs.length === 0) return false;
  return programs.every(isProgramArchived);
}

function toMillis(value: unknown): number {
  if (!value) return 0;
  if (value instanceof Date) return value.getTime();
  const maybeTimestamp = value as { toDate?: () => Date; seconds?: number };
  if (typeof maybeTimestamp.toDate === 'function') return maybeTimestamp.toDate().getTime();
  if (typeof maybeTimestamp.seconds === 'number') return maybeTimestamp.seconds * 1000;
  const parsed = new Date(value as string).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

/** Most recently started non-archived dossier, falling back to the most recent one. */
export function pickCurrentProgram<T extends ProgramLike>(programs: T[]): T | null {
  if (programs.length === 0) return null;
  const byStartDesc = [...programs].sort((a, b) => toMillis(b.start) - toMillis(a.start));
  return byStartDesc.find((program) => !isProgramArchived(program)) || byStartDesc[0];
}
